import { useMemo, useState } from 'react'
import { Vocab } from '../hooks/useVocab'
import DefinitionText from './DefinitionText'
import CloseButton from 'src/components/CloseButton'
import { PlusIcon } from 'lucide-react'

type VocabTooltipProps = {
  isSelected?: boolean
  track?: HTMLElement | undefined
  isLoading: boolean
  vocab?: Vocab
  onClose: () => void
  onAdd: () => void
}

const VocabTooltip = ({
  vocab,
  isLoading,
  onClose,
  onAdd,
  track,
  isSelected = false,
}: VocabTooltipProps) => {
  const [lastPosition, setLastPosition] = useState<
    | {
        top: number
        left: number
      }
    | undefined
  >()

  const isNoDefinition =
    !vocab ||
    !vocab.definitions ||
    vocab.definitions.length === 0

  const elPosition = useMemo(() => {
    if (!track) return

    const rect = track.getBoundingClientRect()
    const doc = document.documentElement

    const top =
      rect.top + window.pageYOffset - doc.clientTop
    const left =
      rect.left + window.pageXOffset - doc.clientLeft

    const position = { top, left }

    setLastPosition(position)
    return position
  }, [track, setLastPosition])

  const { top, left } = useMemo(() => {
    if (!elPosition && lastPosition) return lastPosition

    return elPosition || { top: 0, left: 0 }
  }, [elPosition, lastPosition])

  return (
    <div
      onClick={onClose}
      className={`fixed left-1/2 top-8 z-[100] w-[calc(100%-1rem)] rounded-lg bg-white p-4 shadow-md transition ease-in-out sm:max-w-[23rem] ${
        !track && 'opacity-0'
      }`}
      style={{
        transform: `translate(-50%, ${top}px)`,
        ...(!track ? { zIndex: -50, select: 'none' } : {}),
      }}
    >
      {/* Pointer */}
      <div
        className="relative h-0 w-0 border-l-2 border-r-2 border-b-2 border-l-transparent border-r-transparent"
        style={{ transform: `translateX(${left}px)` }}
      ></div>

      {/* Actions */}
      <div
        className="absolute right-2 bottom-full"
        onClick={() => !isNoDefinition && onAdd()}
      >
        {!isSelected && (
          <div
            className={`flex h-8 w-8 cursor-pointer select-none items-center justify-center rounded-md  bg-[rgb(143,231,138)] font-extrabold text-black shadow-md transition ease-in-out hover:translate-y-1 hover:opacity-80 ${
              isNoDefinition &&
              'pointer-events-none opacity-50'
            }`}
          >
            <PlusIcon />
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={`flex h-full flex-col ${
          !track ? 'opacity-0' : ''
        }`}
      >
        {track && (
          <div className="flex flex-col gap-4">
            <div className="max-h-32 overflow-y-auto">
              <DefinitionText
                vocab={vocab}
                isLoading={isLoading}
              />
            </div>
            {!!(vocab?.numEncounters || 0) && (
              <span className="text-xs">
                Seen {vocab?.numEncounters} time
                {vocab!.numEncounters! > 1 && 's'}
              </span>
            )}
          </div>
        )}
      </div>
      <CloseButton
        onClick={() => {}}
        className="absolute right-2 top-2"
      />
    </div>
  )
}

export default VocabTooltip
